import axios from 'axios';
import type { ClientApplicationInterface } from '../../types/ClientApplication';
import type { Comment, ID } from '../../types/Comment';
import type { FilesToUpload, SignDeveloperAssetPayload, SignedApplicationAssetResponse } from '../../types/Shared';
import { baseUrl, HttpMethods } from '../base';
import type { GetCommentParams, GetCommentsResponse } from '../comments';
import type { IClientApplicationsDashboard } from './types';

const clientApplicationsRootPath = `${baseUrl.SHERPA}/clientApplications`;
export interface PostClientApplicationInterface extends ClientApplicationInterface {
  orgName: string;
  orgAddress: string;
  sponsor: string;
  sponsorAddress: string;
  amountRequested: number;
  socialDesignation: string;
  email: string;
  contactName: string;
  program: string;
  responseDays: string;
}

export async function postClientApplication({ data }: { data: any }) {
  const res = await axios({
    method: HttpMethods.POST,
    url: `${clientApplicationsRootPath}`,
    data,
  });
  return res.data;
}

export async function getClientApplications(params: { page: number; perPage: number }) {
  const res = await axios({
    method: HttpMethods.GET,
    url: `${clientApplicationsRootPath}`,
    params,
  });
  return res.data;
}

export async function getClientApplication({ applicationId }: { applicationId: number }) {
  const res = await axios({
    method: HttpMethods.GET,
    url: `${clientApplicationsRootPath}/${applicationId}`,
  });
  return res.data;
}

export const signClientApplicationUpload = async ({ field, ...rest }: SignDeveloperAssetPayload) => {
  const res = await axios.request<SignedApplicationAssetResponse>({
    url: `${baseUrl.SHERPA}/assets/client_application/sign`,
    method: 'POST',
    data: rest,
  });
  return { ...res.data, field };
};

interface PatchClientApplicationService {
  applicationId: number;
  data: Partial<PatchClientApplicationPayload>;
}
interface PatchClientApplicationPayload extends Partial<ClientApplicationInterface> {
  orgName: string;
  orgAddress: string;
  sponsor: string;
  sponsorAddress: string;
  amountRequested: number;
  socialDesignation: string;
  status: string;
}

export async function patchClientApplication({ applicationId, data }: PatchClientApplicationService) {
  const res = await axios({
    method: HttpMethods.PATCH,
    url: `${clientApplicationsRootPath}/${applicationId}`,
    data,
  });
  return res.data;
}

export async function archiveClientApplication(applicationId: number) {
  const res = await axios({
    method: HttpMethods.PATCH,
    url: `${clientApplicationsRootPath}/${applicationId}/archive`,
  });
  return res.data;
}

export interface ClientApplication extends Comment {
  applicationId: number;
}

export interface GetClientApplicationCommentsParams extends GetCommentParams {
  resource_id: number;
  resource_type: string;
}

export interface GetClientApplicationCommentsPayload {
  params: GetClientApplicationCommentsParams;
}

export const getClientApplicationComments = async (payload: GetClientApplicationCommentsPayload) => {
  const { params } = payload;
  const res = await axios.request<GetCommentsResponse<ClientApplication>>({
    method: 'GET',
    url: '/sherpa/v0/api/comments',
    params: {
      ...params,
    },
  });
  return res.data;
};

export interface PostClientApplicationCommentsPayload {
  data: {
    resource_id: number;
    comment: string;
    resource_type: string;
  };
}

export interface PostClientApplicationComments extends ID {
  resource_id: number;
}

export interface Data<T extends ID> {
  id: T;
}
export interface PostCommentResponse<D extends ID> {
  message: string;
  data: Data<D>;
}

export const postClientApplicationComments = async (payload: PostClientApplicationCommentsPayload) => {
  const { data } = payload;
  const res = await axios.request<PostCommentResponse<PostClientApplicationComments>>({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/comments`,
    data,
  });
  return res.data;
};

export async function addClientApplicationFiles({ applicationId, files }: { applicationId: number; files: FilesToUpload[] }) {
  const res = await axios({
    method: HttpMethods.PUT,
    url: `${baseUrl.SHERPA}/clientApplications/${applicationId}/file`,
    data: { files },
  });
  return res.data;
}

export async function deleteClientApplicationFile({ applicationId, key }: { applicationId: number; key: string }) {
  const res = await axios({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/clientApplications/${applicationId}/file`,
    data: { key },
  });
  return res.data;
}

export async function getClientApplicationsDashboard() {
  const res = await axios.request<IClientApplicationsDashboard>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/analytics/client-application`,
  });
  return res.data;
}
